/**
 * Comprehensive international phone number formatting patterns.
 * Covers major countries and regions with their specific formatting rules.
 */
export const PHONE_FORMATS: Record<string, { pattern: string; maxLength: number }> = {
  // North America (NANP)
  '+1': { pattern: '+1 (XXX) XXX-XXXX', maxLength: 17 },

  // Europe
  '+33': { pattern: '+33 X XX XX XX XX', maxLength: 17 }, // France
  '+49': { pattern: '+49 XXXX XXXXXXX', maxLength: 16 }, // Germany
  '+44': { pattern: '+44 XXXX XXX XXX', maxLength: 16 }, // UK
  '+39': { pattern: '+39 XXX XXX XXXX', maxLength: 16 }, // Italy
  '+34': { pattern: '+34 XXX XXX XXX', maxLength: 15 }, // Spain
  '+31': { pattern: '+31 X XXXX XXXX', maxLength: 15 }, // Netherlands
  '+46': { pattern: '+46 XX XXX XX XX', maxLength: 16 }, // Sweden
  '+47': { pattern: '+47 XXX XX XXX', maxLength: 14 }, // Norway
  '+45': { pattern: '+45 XX XX XX XX', maxLength: 15 }, // Denmark
  '+358': { pattern: '+358 XX XXX XXXX', maxLength: 16 }, // Finland
  '+41': { pattern: '+41 XX XXX XX XX', maxLength: 16 }, // Switzerland
  '+43': { pattern: '+43 XXXX XXXXXX', maxLength: 15 }, // Austria
  '+32': { pattern: '+32 XXX XX XX XX', maxLength: 16 }, // Belgium
  '+48': { pattern: '+48 XXX XXX XXX', maxLength: 15 }, // Poland
  '+420': { pattern: '+420 XXX XXX XXX', maxLength: 16 }, // Czech Republic
  '+36': { pattern: '+36 XX XXX XXXX', maxLength: 15 }, // Hungary
  '+351': { pattern: '+351 XXX XXX XXX', maxLength: 16 }, // Portugal
  '+30': { pattern: '+30 XXX XXX XXXX', maxLength: 16 }, // Greece
  '+353': { pattern: '+353 XX XXX XXXX', maxLength: 16 }, // Ireland
  '+372': { pattern: '+372 XXXX XXXX', maxLength: 14 }, // Estonia
  '+371': { pattern: '+371 XXXX XXXX', maxLength: 14 }, // Latvia
  '+370': { pattern: '+370 XXX XXXXX', maxLength: 14 }, // Lithuania
  '+386': { pattern: '+386 XX XXX XXX', maxLength: 15 }, // Slovenia
  '+385': { pattern: '+385 XX XXX XXXX', maxLength: 16 }, // Croatia
  '+381': { pattern: '+381 XX XXX XXXX', maxLength: 16 }, // Serbia
  '+359': { pattern: '+359 XX XXX XXXX', maxLength: 16 }, // Bulgaria
  '+40': { pattern: '+40 XXX XXX XXX', maxLength: 15 }, // Romania
  '+90': { pattern: '+90 XXX XXX XX XX', maxLength: 17 }, // Turkey
  '+7': { pattern: '+7 XXX XXX-XX-XX', maxLength: 16 }, // Russia/Kazakhstan
  '+380': { pattern: '+380 XX XXX XX XX', maxLength: 17 }, // Ukraine
  '+375': { pattern: '+375 XX XXX-XX-XX', maxLength: 17 }, // Belarus

  // Asia Pacific
  '+86': { pattern: '+86 XXX XXXX XXXX', maxLength: 17 }, // China
  '+81': { pattern: '+81 XX-XXXX-XXXX', maxLength: 16 }, // Japan
  '+82': { pattern: '+82 XX-XXXX-XXXX', maxLength: 16 }, // South Korea
  '+91': { pattern: '+91 XXXXX XXXXX', maxLength: 15 }, // India
  '+65': { pattern: '+65 XXXX XXXX', maxLength: 13 }, // Singapore
  '+60': { pattern: '+60 XX-XXX XXXX', maxLength: 15 }, // Malaysia
  '+66': { pattern: '+66 XX XXX XXXX', maxLength: 15 }, // Thailand
  '+84': { pattern: '+84 XXX XXX XXXX', maxLength: 16 }, // Vietnam
  '+63': { pattern: '+63 XXX XXX XXXX', maxLength: 16 }, // Philippines
  '+62': { pattern: '+62 XXX-XXXX-XXXX', maxLength: 17 }, // Indonesia
  '+61': { pattern: '+61 XXX XXX XXX', maxLength: 15 }, // Australia
  '+64': { pattern: '+64 XX XXX XXXX', maxLength: 15 }, // New Zealand
  '+852': { pattern: '+852 XXXX XXXX', maxLength: 14 }, // Hong Kong
  '+853': { pattern: '+853 XXXX XXXX', maxLength: 14 }, // Macau
  '+886': { pattern: '+886 XXX XXX XXX', maxLength: 16 }, // Taiwan
  '+92': { pattern: '+92 XXX XXX XXXX', maxLength: 16 }, // Pakistan
  '+880': { pattern: '+880 XXXX-XXXXXX', maxLength: 16 }, // Bangladesh
  '+94': { pattern: '+94 XX XXX XXXX', maxLength: 15 }, // Sri Lanka
  '+977': { pattern: '+977 XXX-XXX-XXXX', maxLength: 17 }, // Nepal
  '+95': { pattern: '+95 X XXX XXXX', maxLength: 14 }, // Myanmar
  '+855': { pattern: '+855 XX XXX XXX', maxLength: 15 }, // Cambodia
  '+856': { pattern: '+856 XX XXX XXX', maxLength: 15 }, // Laos
  '+673': { pattern: '+673 XXX XXXX', maxLength: 13 }, // Brunei
  '+975': { pattern: '+975 X XXX XXXX', maxLength: 15 }, // Bhutan
  '+960': { pattern: '+960 XXX-XXXX', maxLength: 13 }, // Maldives

  // Middle East & Africa
  '+971': { pattern: '+971 XX XXX XXXX', maxLength: 16 }, // UAE
  '+966': { pattern: '+966 XX XXX XXXX', maxLength: 16 }, // Saudi Arabia
  '+974': { pattern: '+974 XXXX XXXX', maxLength: 14 }, // Qatar
  '+965': { pattern: '+965 XXXX XXXX', maxLength: 14 }, // Kuwait
  '+973': { pattern: '+973 XXXX XXXX', maxLength: 14 }, // Bahrain
  '+968': { pattern: '+968 XXXX XXXX', maxLength: 14 }, // Oman
  '+962': { pattern: '+962 X XXXX XXXX', maxLength: 16 }, // Jordan
  '+961': { pattern: '+961 XX XXX XXX', maxLength: 15 }, // Lebanon
  '+963': { pattern: '+963 XXX XXX XXX', maxLength: 16 }, // Syria
  '+964': { pattern: '+964 XXX XXX XXXX', maxLength: 17 }, // Iraq
  '+98': { pattern: '+98 XXX XXX XXXX', maxLength: 16 }, // Iran
  '+93': { pattern: '+93 XX XXX XXXX', maxLength: 15 }, // Afghanistan
  '+972': { pattern: '+972 XX-XXX-XXXX', maxLength: 16 }, // Israel
  '+970': { pattern: '+970 XXX XXX XXX', maxLength: 16 }, // Palestine
  '+20': { pattern: '+20 XXX XXX XXXX', maxLength: 16 }, // Egypt
  '+27': { pattern: '+27 XX XXX XXXX', maxLength: 15 }, // South Africa
  '+234': { pattern: '+234 XXX XXX XXXX', maxLength: 17 }, // Nigeria
  '+254': { pattern: '+254 XXX XXX XXX', maxLength: 16 }, // Kenya
  '+255': { pattern: '+255 XXX XXX XXX', maxLength: 16 }, // Tanzania
  '+256': { pattern: '+256 XXX XXX XXX', maxLength: 16 }, // Uganda
  '+250': { pattern: '+250 XXX XXX XXX', maxLength: 16 }, // Rwanda
  '+251': { pattern: '+251 XX XXX XXXX', maxLength: 16 }, // Ethiopia
  '+212': { pattern: '+212 XXX-XXXXXX', maxLength: 15 }, // Morocco
  '+213': { pattern: '+213 XXX XXX XXX', maxLength: 16 }, // Algeria
  '+216': { pattern: '+216 XX XXX XXX', maxLength: 15 }, // Tunisia
  '+218': { pattern: '+218 XX-XXXXXXX', maxLength: 15 }, // Libya
  '+233': { pattern: '+233 XXX XXX XXX', maxLength: 16 }, // Ghana
  '+225': { pattern: '+225 XX XX XX XX', maxLength: 16 }, // Ivory Coast
  '+221': { pattern: '+221 XX XXX XX XX', maxLength: 17 }, // Senegal
  '+229': { pattern: '+229 XX XX XX XX', maxLength: 16 }, // Benin
  '+228': { pattern: '+228 XX XX XX XX', maxLength: 16 }, // Togo
  '+227': { pattern: '+227 XX XX XX XX', maxLength: 16 }, // Niger
  '+226': { pattern: '+226 XX XX XX XX', maxLength: 16 }, // Burkina Faso
  '+223': { pattern: '+223 XX XX XX XX', maxLength: 16 }, // Mali
  '+222': { pattern: '+222 XX XX XX XX', maxLength: 16 }, // Mauritania
  '+220': { pattern: '+220 XXX XXXX', maxLength: 13 }, // Gambia
  '+224': { pattern: '+224 XXX XXX XXX', maxLength: 16 }, // Guinea
  '+245': { pattern: '+245 XXX XXXX', maxLength: 13 }, // Guinea-Bissau
  '+238': { pattern: '+238 XXX XX XX', maxLength: 14 }, // Cape Verde
  '+239': { pattern: '+239 XXX XXXX', maxLength: 13 }, // São Tomé and Príncipe
  '+240': { pattern: '+240 XXX XXX XXX', maxLength: 16 }, // Equatorial Guinea
  '+241': { pattern: '+241 XX XX XX XX', maxLength: 16 }, // Gabon
  '+242': { pattern: '+242 XX XXX XXXX', maxLength: 16 }, // Republic of Congo
  '+243': { pattern: '+243 XXX XXX XXX', maxLength: 16 }, // Democratic Republic of Congo
  '+244': { pattern: '+244 XXX XXX XXX', maxLength: 16 }, // Angola
  '+258': { pattern: '+258 XX XXX XXXX', maxLength: 16 }, // Mozambique
  '+260': { pattern: '+260 XX XXX XXXX', maxLength: 16 }, // Zambia
  '+263': { pattern: '+263 XX XXX XXXX', maxLength: 16 }, // Zimbabwe
  '+264': { pattern: '+264 XX XXX XXXX', maxLength: 16 }, // Namibia
  '+267': { pattern: '+267 XX XXX XXX', maxLength: 15 }, // Botswana
  '+268': { pattern: '+268 XX XX XXXX', maxLength: 15 }, // Eswatini
  '+266': { pattern: '+266 XX XXX XXX', maxLength: 15 }, // Lesotho
  '+230': { pattern: '+230 XXXX XXXX', maxLength: 14 }, // Mauritius
  '+248': { pattern: '+248 X XX XX XX', maxLength: 15 }, // Seychelles
  '+261': { pattern: '+261 XX XX XXX XX', maxLength: 17 }, // Madagascar
  '+269': { pattern: '+269 XXX XX XX', maxLength: 14 }, // Comoros
  '+262': { pattern: '+262 XXX XX XX XX', maxLength: 17 }, // Réunion/Mayotte

  // Americas
  '+52': { pattern: '+52 XX XXXX XXXX', maxLength: 16 }, // Mexico
  '+54': { pattern: '+54 XX XXXX-XXXX', maxLength: 16 }, // Argentina
  '+55': { pattern: '+55 XX XXXXX-XXXX', maxLength: 17 }, // Brazil
  '+56': { pattern: '+56 X XXXX XXXX', maxLength: 15 }, // Chile
  '+57': { pattern: '+57 XXX XXX XXXX', maxLength: 16 }, // Colombia
  '+58': { pattern: '+58 XXX-XXX-XXXX', maxLength: 16 }, // Venezuela
  '+51': { pattern: '+51 XXX XXX XXX', maxLength: 15 }, // Peru
  '+593': { pattern: '+593 XX XXX XXXX', maxLength: 16 }, // Ecuador
  '+591': { pattern: '+591 X XXX XXXX', maxLength: 15 }, // Bolivia
  '+595': { pattern: '+595 XXX XXX XXX', maxLength: 16 }, // Paraguay
  '+598': { pattern: '+598 XXXX XXXX', maxLength: 14 }, // Uruguay
  '+597': { pattern: '+597 XXX-XXXX', maxLength: 13 }, // Suriname
  '+592': { pattern: '+592 XXX XXXX', maxLength: 13 }, // Guyana
  '+594': { pattern: '+594 XXX XX XX XX', maxLength: 17 }, // French Guiana
  '+503': { pattern: '+503 XXXX XXXX', maxLength: 14 }, // El Salvador
  '+502': { pattern: '+502 XXXX XXXX', maxLength: 14 }, // Guatemala
  '+504': { pattern: '+504 XXXX XXXX', maxLength: 14 }, // Honduras
  '+505': { pattern: '+505 XXXX XXXX', maxLength: 14 }, // Nicaragua
  '+506': { pattern: '+506 XXXX XXXX', maxLength: 14 }, // Costa Rica
  '+507': { pattern: '+507 XXXX XXXX', maxLength: 14 }, // Panama
  '+508': { pattern: '+508 XX XX XX', maxLength: 13 }, // Saint Pierre and Miquelon
  '+509': { pattern: '+509 XX XX XXXX', maxLength: 15 }, // Haiti
  '+590': { pattern: '+590 XXX XX XX XX', maxLength: 17 }, // Guadeloupe
  '+596': { pattern: '+596 XXX XX XX XX', maxLength: 17 }, // Martinique

  // Other regions
  '+374': { pattern: '+374 XX XXX XXX', maxLength: 15 }, // Armenia
  '+995': { pattern: '+995 XXX XXX XXX', maxLength: 16 }, // Georgia
  '+994': { pattern: '+994 XX XXX XX XX', maxLength: 17 }, // Azerbaijan
  '+992': { pattern: '+992 XX XXX XXXX', maxLength: 16 }, // Tajikistan
  '+993': { pattern: '+993 XX XXXXXX', maxLength: 14 }, // Turkmenistan
  '+998': { pattern: '+998 XX XXX XX XX', maxLength: 17 }, // Uzbekistan
  '+996': { pattern: '+996 XXX XXX XXX', maxLength: 16 }, // Kyrgyzstan
  '+976': { pattern: '+976 XXXX XXXX', maxLength: 14 }, // Mongolia
};

/**
 * Automatically generates validation rules from phone formatting patterns.
 * This ensures all countries in PHONE_FORMATS have consistent validation
 * and eliminates the need for manual maintenance of duplicate validation rules.
 */
export function getPhoneValidationForCountry(
  countryCode: string
): { minDigits: number; maxDigits: number } | null {
  const format = PHONE_FORMATS[countryCode];
  if (!format) return null;

  // Count the number of X's in the pattern to get expected digit count
  const expectedDigits = (format.pattern.match(/X/g) || []).length;

  // Use exact digit count for strict validation
  return {
    minDigits: expectedDigits, // Exact count required
    maxDigits: expectedDigits, // No extra digits allowed
  };
}
